"use client";

import clsx from "clsx";
import { Monogram } from "@/components/persona/Monogram";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * M4 — Aurora's catalogue-analytics view. Surfaces what she sees BEFORE
 * Al Madar's proposal lands at T1 1.6: the tokenised catalogue link
 * she published is getting viewed, by whom, with what intent signals.
 *
 * Used as the exporter counterpart for T1 0.5 (Public catalogue page).
 */
export function CatalogueAnalytics({ className }: { className?: string }) {
  const t = useTranslations("catalogueAnalytics");
  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <div className="glass p-5">
        <div className="flex items-center gap-4">
          <Monogram name="Aurora Aves Brasil" flag="BR" size={36} accent="var(--state-verified)" />
          <div className="flex-1">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
              {t("kickerYourView")}
            </p>
            <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">
              {t("heading")}
            </h3>
            <p className="mt-1 text-[12px] text-orbitra-teal-soft">
              orbitra.trade/c/aurora-aves/halal-whole-chicken · published 14 days ago
            </p>
          </div>
          <span className="rounded-full border border-state-traced/40 bg-state-traced/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-traced">
            {t("badgeViewers")}
          </span>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-3 gap-4">
        <div className="glass col-span-2 flex flex-col p-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
            {t("eyeballsKicker")}
          </p>

          <ul className="mt-3 flex flex-col gap-2 text-[11px]">
            <ViewerRow flag="AE" name={t("viewer1Name")} hint={t("viewer1Hint")} signal={t("viewer1Signal")} hot />
            <ViewerRow flag="AE" name={t("viewer2Name")} hint={t("viewer2Hint")} signal={t("viewer2Signal")} />
            <ViewerRow flag="AE" name={t("viewer3Name")} hint={t("viewer3Hint")} signal={t("viewer3Signal")} />
            <ViewerRow flag="SA" name={t("viewer4Name")} hint={t("viewer4Hint")} signal={t("viewer4Signal")} />
            <ViewerRow flag="EG" name={t("viewer5Name")} hint={t("viewer5Hint")} signal={t("viewer5Signal")} />
          </ul>

          <div className="mt-auto rounded-md border border-state-verified/30 bg-state-verified/8 px-3 py-2 text-[11px] text-orbitra-paper">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
              {t("echoLabel")}
            </p>
            <p className="mt-1">
              {t("echoBody")}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="glass p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">
              {t("funnelKicker")}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-[11px]">
              <FunnelRow label={t("funnelTokenisedOpens")}       value="186" />
              <FunnelRow label={t("funnelCatalogueViews")}       value="142" />
              <FunnelRow label={t("funnelVerifiedBuyerViews")}   value="42" />
              <FunnelRow label={t("funnelProposalBuilderOpens")} value="3" highlight />
              <FunnelRow label={t("funnelLiveProposals")}        value="1" highlight />
            </ul>
          </div>

          <div className="glass p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">
              {t("jurisdictionKicker")}
            </p>
            <ul className="mt-2 flex flex-col gap-1 text-[11px]">
              <JurisdictionRow flag="AE" share={48} label={t("jurisdictionUae")} />
              <JurisdictionRow flag="SA" share={22} label={t("jurisdictionSaudiArabia")} />
              <JurisdictionRow flag="EG" share={14} label={t("jurisdictionEgypt")} />
              <JurisdictionRow flag="QA" share={10} label={t("jurisdictionQatar")} />
              <JurisdictionRow flag="OM" share={6}  label={t("jurisdictionOman")} />
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function ViewerRow({
  flag, name, hint, signal, hot,
}: {
  flag: string; name: string; hint: string; signal: string; hot?: boolean;
}) {
  return (
    <li className={clsx(
      "flex items-start gap-3 rounded-md border px-3 py-2",
      hot
        ? "border-state-verified/50 bg-state-verified/10 shadow-[0_0_18px_var(--state-verified-glow)]"
        : "border-orbitra-muted/30 bg-orbitra-ink/40",
    )}>
      <span className="font-mono text-[9px] text-orbitra-muted">{flag}</span>
      <div className="flex-1">
        <p className={clsx("text-[11px] font-semibold", hot ? "text-state-verified" : "text-orbitra-paper")}>{name}</p>
        <p className="text-[10px] text-orbitra-muted">{hint}</p>
      </div>
      <span className={clsx(
        "rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider",
        hot
          ? "border border-state-verified/50 bg-state-verified/15 text-state-verified"
          : "border border-orbitra-muted/40 bg-orbitra-muted/10 text-orbitra-muted",
      )}>
        {signal}
      </span>
    </li>
  );
}

function FunnelRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <li className={clsx(
      "flex items-center justify-between rounded border px-2 py-1",
      highlight
        ? "border-state-verified/40 bg-state-verified/8 text-state-verified"
        : "border-orbitra-muted/20 bg-orbitra-ink/30 text-orbitra-muted",
    )}>
      <span className="font-mono text-[9px] uppercase tracking-[0.18em]">{label}</span>
      <span className={clsx("font-display", highlight && "font-semibold")}>{value}</span>
    </li>
  );
}

function JurisdictionRow({ flag, share, label }: { flag: string; share: number; label: string }) {
  return (
    <li className="flex items-center gap-2 text-orbitra-paper">
      <span className="font-mono text-[9px] text-orbitra-muted">{flag}</span>
      <span className="flex-1 text-[10px]">{label}</span>
      <div className="h-1 w-12 overflow-hidden rounded-full bg-orbitra-ink/60">
        <div className="h-full rounded-full bg-state-traced" style={{ width: `${share}%` }} />
      </div>
      <span className="w-8 text-end font-mono text-[10px] text-state-traced">{share}%</span>
    </li>
  );
}
